/**
 * Robust domain detection utilities for DoubleMe/MyCal routing
 */

export interface DomainInfo {
  isMycal: boolean;
  isApp: boolean;
  isMarketing: boolean;
  isDev: boolean;
  hostname: string;
  originalUrl: string;
}

/**
 * Detects if current request is for mycal.live domain
 * Handles all edge cases: subdomains, ports, dev environments
 */
export const detectDomain = (): DomainInfo => {
  // Handle SSR/Node environments
  if (typeof window === 'undefined') {
    return {
      isMycal: false,
      isApp: false,
      isMarketing: false,
      isDev: true,
      hostname: 'localhost',
      originalUrl: ''
    };
  }

  const hostname = window.location.hostname.toLowerCase();
  const port = window.location.port;
  const originalUrl = window.location.href;

  // Development environment detection
  const isDev = hostname === 'localhost' ||
                hostname === '127.0.0.1' ||
                hostname.includes('.local') ||
                hostname.includes('ngrok') ||
                hostname.includes('vercel.app') ||
                hostname.includes('netlify.app');

  // MyCal domain detection (bulletproof)
  const isMycal =
    // Exact matches
    hostname === 'mycal.live' ||
    hostname === 'www.mycal.live' ||
    // Subdomains
    hostname.endsWith('.mycal.live') ||
    // Dev environment override via URL param
    (isDev && new URLSearchParams(window.location.search).get('domain') === 'mycal') ||
    // Dev environment via specific port (for testing)
    (isDev && port === '5174');

  // App domain detection (app.getadouble.com)
  const isApp =
    hostname === 'app.getadouble.com' ||
    (isDev && new URLSearchParams(window.location.search).get('domain') === 'app');

  // Marketing domain detection (www.getadouble.com or getadouble.com)
  const isMarketing =
    hostname === 'getadouble.com' ||
    hostname === 'www.getadouble.com' ||
    (isDev && new URLSearchParams(window.location.search).get('domain') === 'marketing') ||
    // Default to marketing for any other getadouble.com subdomain that isn't 'app'
    (!isMycal && !isApp && !isDev);

  return {
    isMycal,
    isApp,
    isMarketing,
    isDev,
    hostname,
    originalUrl
  };
};

/**
 * Get the appropriate base URL for API calls based on domain
 */
export const getApiBaseUrl = (): string => {
  const domain = detectDomain();
  
  // In development, always use local or environment variable
  if (domain.isDev) {
    return import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
  }
  
  // Production: same API regardless of domain
  return import.meta.env.VITE_API_BASE_URL || 'https://doubleme-production.up.railway.app';
};

/**
 * Check if we should show authentication features
 */
export const shouldShowAuth = (): boolean => {
  const { isMycal } = detectDomain();
  return !isMycal; // MyCal is public-only, no auth
};

/**
 * Get the appropriate redirect URL for cross-domain navigation
 */
export const getCrossDomainUrl = (path: string, targetDomain: 'mycal' | 'doubleme'): string => {
  const currentDomain = detectDomain();
  
  if (currentDomain.isDev) {
    // In dev, use URL params to simulate domain switching
    const baseUrl = window.location.origin;
    const domainParam = targetDomain === 'mycal' ? '?domain=mycal' : '';
    return `${baseUrl}${path}${domainParam}`;
  }
  
  // Production domain switching
  const targetHost = targetDomain === 'mycal' ? 'mycal.live' : 'getadouble.com';
  return `https://${targetHost}${path}`;
};

/**
 * Validate that the current domain configuration is correct
 */
export const validateDomainConfig = (): { isValid: boolean; warnings: string[] } => {
  const domain = detectDomain();
  const warnings: string[] = [];
  
  // Check for potential issues
  if (domain.isMycal && domain.hostname.includes('localhost')) {
    warnings.push('MyCal domain detected on localhost - ensure this is intentional for testing');
  }
  
  if (!domain.isDev && !window.location.protocol.includes('https')) {
    warnings.push('Production domain should use HTTPS');
  }
  
  // Check environment variables
  if (domain.isMycal && !import.meta.env.VITE_SUPABASE_ANON_KEY) {
    warnings.push('MyCal domain requires Supabase configuration');
  }
  
  return {
    isValid: warnings.length === 0,
    warnings
  };
};

/**
 * Debug information for troubleshooting domain detection
 */
export const getDomainDebugInfo = () => {
  if (typeof window === 'undefined') return 'SSR Environment';
  
  const domain = detectDomain();
  const validation = validateDomainConfig();
  
  return {
    ...domain,
    validation,
    location: {
      href: window.location.href,
      hostname: window.location.hostname,
      port: window.location.port,
      protocol: window.location.protocol,
      search: window.location.search
    },
    env: {
      NODE_ENV: import.meta.env.NODE_ENV,
      DEV: import.meta.env.DEV,
      PROD: import.meta.env.PROD
    }
  };
};